import AppCard from '@nxsys/components/AppCard';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import {
  defaultHolidayPayCalcOptions,
  defaultLeaveYearStart,
  getHolidayPaymentMethodOptions,
  getUmbrellaHolidayPaymentMethodOptions,
  leaveYearStartDateOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
// import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import {
  getAppConfig,
  PAYMENT_MODEL_TEMPLATES,
  PAYSLIP_TEMPLATES,
} from '@nxsys/utils';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle, useMemo } from 'react';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import {
  getDemoCisTemplateLayout,
  getDemoTemplateLayout,
  RCT_DEFAULT_DEMO_LAYOUT,
} from '@nxsys/constants/templateFiles';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { some } from 'lodash';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';

export type PaymentModelValues = {
  // NX-6284: Ireland-only leave year start, default 1 April. Encoded as
  // "day-month" (toLeaveYearStart/fromLeaveYearStart) so one dropdown
  // carries both numbers the API still wants.
  holidayLeaveYearStart: Option<string> | null;

  // PAYE
  payeEnabled: boolean;
  payeHolidayPay: boolean | null;
  payeHolidayPayRate: string;
  payeHolidayPayMethod: Option<string> | null;
  payeHolidayPayCalculationRule: Option<string> | null;
  payePayslipTemplate: Option<string> | null;
  payeIncludedInTimesheetRate: boolean | null;

  // Umbrella
  umbrellaEnabled: boolean;
  umbrellaSplitSalaryBonus: boolean | null;
  umbrellaPayslipTemplate: Option<string> | null;
  umbrellaHolidayPay: boolean | null;
  umbrellaHolidayPayRate: string;
  umbrellaHolidayPayMethod: Option<string> | null;
  umbrellaHolidayPayCalculationRule: Option<string> | null;
  umbrellaReclaimAppLevyCosts: boolean | null;
  umbrellaAccrueEmploymentCosts: boolean | null;

  // PAYE Hybrid
  payeHybridEnabled: boolean;
  hybridPayePayslipTemplate: Option<string> | null;

  // Umbrella Hybrid
  umbrellaHybridEnabled: boolean;
  umbrellaHybridReclaimAppLevyCosts: boolean | null;
  hybridPayslipTemplate: Option<string> | null;

  // CIS
  cisEnabled: boolean;
  cisPayslipTemplate: Option<string> | null;

  // PAYE FTH
  payeFTHEnabled: boolean;
  taxFreePaymentDesc: string;
  payeFthMarginAdjustment: boolean | null;
  payeFTHPayslipTemplate: Option<string> | null;
};

export type PaymentModelFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => PaymentModelValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<PaymentModelFormRef>;
  initialValues: Partial<PaymentModelValues>;
  stepNumber: number;
};

const PaymentModel: React.FC<Props> = ({
  formRef,
  initialValues,
  stepNumber,
}) => {
  // const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;
  const defaultPaymentModel = getAppConfig('paymentModels');
  const hasPayeFTH = some(
    defaultPaymentModel,
    (item) => item.value === PAYMENT_MODEL_ENUM.PAYE_FTH,
  );

  const [form] = Form.useForm<PaymentModelValues>();
  const watchValues = Form.useWatch([], form);

  const payeHolidayPayMethodOptions = useMemo(
    () => getHolidayPaymentMethodOptions(isIreland, watchValues?.payeHolidayPayMethod?.value),
    [isIreland, watchValues?.payeHolidayPayMethod?.value],
  );
  // Umbrella never offers 52-week averaging or the 8% irregular method
  // (NX-6284 made that PAYE-only), in any country.
  const umbrellaHolidayPayMethodOptions = useMemo(
    () =>
      getUmbrellaHolidayPaymentMethodOptions(
        watchValues?.umbrellaHolidayPayMethod?.value,
      ),
    [watchValues?.umbrellaHolidayPayMethod?.value],
  );

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="PaymentModel">
      <StepTitle step={stepNumber} title="Payment Model" description="" />
      <div className="mt-[24px]">
        <div className="text-[14px] text-primary-600 italic mb-[24px]">
          *Choose which payment models apply to your company
        </div>
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            umbrellaPayslipTemplate: PAYSLIP_TEMPLATES.DEFAULT,
            payePayslipTemplate: PAYSLIP_TEMPLATES.DEFAULT,
            hybridPayslipTemplate: PAYSLIP_TEMPLATES.DEFAULT,
            cisPayslipTemplate: PAYSLIP_TEMPLATES.DEFAULT,
            hybridPayePayslipTemplate: PAYSLIP_TEMPLATES.DEFAULT,
            payeFTHPayslipTemplate: PAYSLIP_TEMPLATES.DEFAULT,
            ...(!isIreland && !isBureau ? {
              payeHolidayPay: true,
              payeHolidayPayRate: '12.07',
              payeHolidayPayMethod: { label: 'Advanced', value: 'ROLLED_UP' },
              payeHolidayPayCalculationRule: { label: 'All Earnings', value: 'ALL_EARNINGS' },
              umbrellaHolidayPay: true,
              umbrellaHolidayPayRate: '12.07',
              umbrellaHolidayPayMethod: { label: 'Advanced', value: 'ROLLED_UP' },
              umbrellaHolidayPayCalculationRule: { label: 'All Earnings', value: 'ALL_EARNINGS' },
            } : {}),
            ...(isBureau ? { payeEnabled: true } : {}),
            // NX-5228: Irish statutory holiday pay is 8% (editable), not the UK
            // 12.07% set above — the backend hard-codes 8% for Ireland too.
            // Umbrella-only: a bureau tenant has no Umbrella card, so seeding an
            // umbrella rate there just puts an unused value in the payload.
            ...(isIreland && !isBureau ? { umbrellaHolidayPayRate: '8' } : {}),
            // NX-6284: default leave year start 1 April for Ireland.
            ...(isIreland
              ? {
                  holidayLeaveYearStart: leaveYearStartDateOptions.find(
                    (item) => item.value === defaultLeaveYearStart,
                  ),
                }
              : {}),
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <div className="space-y-[24px]">
            {/* PAYE Section */}
            <AppCard className="p-[16px]">
              <div className="flex items-center gap-x-[8px]">
                <Form.Item name="payeEnabled" className="mb-0" valuePropName="checked">
                  <AppSwitch disabled={isUK} />
                </Form.Item>
                <div className="text-[14px] font-[500] text-[#5c656e]">
                  PAYE
                </div>
              </div>
              {watchValues?.payeEnabled && (
                <div className="mt-[24px] space-y-[16px]">
                  {!isIreland && (
                    <>
                      <div className="flex items-center gap-x-[8px] mb-[16px]">
                        <IconWrapper icon="IconSetting" />
                        <div className="text-[16px] font-semibold">
                          Holiday Pay Setting
                        </div>
                      </div>
                      <Row gutter={16}>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Holiday Pay"
                            name="payeHolidayPay"
                            dependencies={['payeEnabled']}
                          >
                            <AppRadioGroup options={yesNoOptions} />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Holiday Pay Rate %"
                            name="payeHolidayPayRate"
                            dependencies={['payeEnabled']}
                          >
                            <AppPercentageInput
                              placeholder="Enter holiday pay rate"
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Holiday Pay Method"
                            name="payeHolidayPayMethod"
                            dependencies={['payeEnabled']}
                          >
                            <AppSelect
                              placeholder="Select holiday pay method"
                              options={payeHolidayPayMethodOptions}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Holiday Pay Calculation Rule"
                            name="payeHolidayPayCalculationRule"
                            dependencies={['payeEnabled']}
                          >
                            <AppSelect
                              placeholder="Select calculation rule"
                              options={defaultHolidayPayCalcOptions}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                  {/* NX-6284: Ireland-only leave year start, default 1
                      April. Ireland's PAYE holiday pay method is hard-coded
                      to the 8% irregular method server-side (no selectable
                      dropdown here), so this sits where that dropdown would
                      otherwise be. One searchable dropdown over every
                      calendar date (29 Feb excluded). */}
                  {isIreland && (
                    <Row gutter={16}>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="Leave Year Start"
                          name="holidayLeaveYearStart"
                        >
                          <AppSelect
                            placeholder="Select date"
                            options={leaveYearStartDateOptions}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                  <Row gutter={16}>
                    <Col sm={24} md={12}>
                      <div className="flex items-end gap-x-[8px]">
                        <div className="flex-1">
                          <Form.Item
                            label="Payslip Template"
                            name="payePayslipTemplate"
                            dependencies={['payeEnabled']}
                            className="mb-0"
                          >
                            <AppSelect
                              placeholder="Select payslip template"
                              options={PAYMENT_MODEL_TEMPLATES?.PAYE}
                            />
                          </Form.Item>
                        </div>
                        {!!watchValues?.payePayslipTemplate?.value && (
                          <div
                            className="cursor-pointer mb-[12px]"
                            onClick={() =>
                              window.open(
                                getDemoTemplateLayout(
                                  watchValues?.payePayslipTemplate?.value || '',
                                  PAYMENT_MODEL_ENUM.PAYEE,
                                  isIreland,
                                ),
                                '_blank',
                              )
                            }
                          >
                            <Icons.IconEye />
                          </div>
                        )}
                      </div>
                    </Col>
                    {!isIreland && (
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="Included in Timesheet Rate"
                          name="payeIncludedInTimesheetRate"
                          dependencies={['payeEnabled']}
                        >
                          <AppRadioGroup options={yesNoOptions} />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </AppCard>

            {!isBureau && (
              <>
                {/* Umbrella Section */}
                <AppCard className="p-[16px]">
                  <div className="flex items-center gap-x-[8px]">
                    <Form.Item name="umbrellaEnabled" className="mb-0" valuePropName="checked">
                      <AppSwitch />
                    </Form.Item>
                    <div className="text-[14px] font-[500] text-[#5c656e]">
                      Umbrella
                    </div>
                  </div>
                  {watchValues?.umbrellaEnabled && (
                    <div className="mt-[24px] space-y-[24px]">
                      <div className="space-y-[16px]">
                        <div className="flex items-center gap-x-[8px] mb-[16px]">
                          <IconWrapper icon="IconFileText" />
                          <div className="text-[16px] font-semibold">
                            Processing Rules
                          </div>
                        </div>
                        <Row gutter={16}>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Split Salary & Bonus"
                              name="umbrellaSplitSalaryBonus"
                              dependencies={['umbrellaEnabled']}
                            >
                              <AppRadioGroup options={yesNoOptions} />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <div className="flex items-end gap-x-[8px]">
                              <div className="flex-1">
                                <Form.Item
                                  label="Payslip Template"
                                  name="umbrellaPayslipTemplate"
                                  dependencies={['umbrellaEnabled']}
                                  className="mb-0"
                                >
                                  <AppSelect
                                    placeholder="Select payslip template"
                                    options={
                                      // NX-5228: Ireland has one umbrella template;
                                      // offering Layout 2/3 would preview UK layouts.
                                      isIreland
                                        ? [PAYSLIP_TEMPLATES.DEFAULT]
                                        : PAYMENT_MODEL_TEMPLATES?.UMBRELLA
                                    }
                                  />
                                </Form.Item>
                              </div>
                              {/* NX-5228: no Ireland-Umbrella preview asset yet
                                  (phase 7) — getDemoTemplateLayout would fall
                                  through to the UK Umbrella demo, which is
                                  misleading for an Irish payslip. */}
                              {!!watchValues?.umbrellaPayslipTemplate?.value &&
                                !isIreland && (
                                  <div
                                    className="cursor-pointer mb-[12px]"
                                    onClick={() =>
                                      window.open(
                                        getDemoTemplateLayout(
                                          watchValues?.umbrellaPayslipTemplate
                                            ?.value || '',
                                          PAYMENT_MODEL_ENUM.UMBRELLA,
                                          isIreland,
                                        ),
                                        '_blank',
                                      )
                                    }
                                  >
                                    <Icons.IconEye />
                                  </div>
                                )}
                            </div>
                          </Col>
                        </Row>
                      </div>
                      <div className="space-y-[16px]">
                        <div className="flex items-center gap-x-[8px] mb-[16px]">
                          <IconWrapper icon="IconSetting" />
                          <div className="text-[16px] font-semibold">
                            Holiday Pay Setting
                          </div>
                        </div>
                        <Row gutter={16}>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Holiday Pay"
                              name="umbrellaHolidayPay"
                              dependencies={['umbrellaEnabled']}
                            >
                              <AppRadioGroup options={yesNoOptions} />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Holiday Pay Rate %"
                              name="umbrellaHolidayPayRate"
                              dependencies={['umbrellaEnabled']}
                            >
                              <AppPercentageInput
                                placeholder="Enter holiday pay rate"
                              />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Holiday Pay Method"
                              name="umbrellaHolidayPayMethod"
                              dependencies={['umbrellaEnabled']}
                            >
                              <AppSelect
                                placeholder="Select holiday pay method"
                                options={umbrellaHolidayPayMethodOptions}
                              />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Holiday Pay Calculation Rule"
                              name="umbrellaHolidayPayCalculationRule"
                              dependencies={['umbrellaEnabled']}
                            >
                              <AppSelect
                                placeholder="Select calculation rule"
                                options={defaultHolidayPayCalcOptions}
                              />
                            </Form.Item>
                          </Col>
                          {/* NX-5228: App Levy / Accrue Employment Costs are
                              UK-umbrella-only concepts, not offered in Ireland. */}
                          {!isIreland && (
                            <>
                              <Col sm={24} md={12}>
                                <Form.Item
                                  label="Reclaim App Levy Costs"
                                  name="umbrellaReclaimAppLevyCosts"
                                  dependencies={['umbrellaEnabled']}
                                >
                                  <AppRadioGroup options={yesNoOptions} />
                                </Form.Item>
                              </Col>
                              <Col sm={24} md={12}>
                                <Form.Item
                                  label="Accrue Employment Costs"
                                  name="umbrellaAccrueEmploymentCosts"
                                  dependencies={['umbrellaEnabled']}
                                >
                                  <AppRadioGroup options={yesNoOptions} />
                                </Form.Item>
                              </Col>
                            </>
                          )}
                        </Row>
                      </div>
                    </div>
                  )}
                </AppCard>

                {/* NX-5228: Ireland gets Umbrella only. Hybrid, CIS and FTH stay
                    UK-umbrella-tenant features; Ireland has its own RCT card below. */}
                {!isIreland && (
                  <>
                {/* PAYE Hybrid Section */}
                <AppCard className="p-[16px]">
                  <div className="flex items-center gap-x-[8px]">
                    <Form.Item name="payeHybridEnabled" className="mb-0" valuePropName="checked">
                      <AppSwitch />
                    </Form.Item>
                    <div className="text-[14px] font-[500] text-[#5c656e]">
                      PAYE Hybrid
                    </div>
                  </div>
                  {watchValues?.payeHybridEnabled && (
                    <div className="mt-[24px]">
                      <Row gutter={[24, 24]}>
                        <Col span={24}>
                          <div className="flex items-end gap-x-[8px]">
                            <div className="flex-1">
                              <Form.Item
                                label="Payslip Template"
                                name="hybridPayePayslipTemplate"
                                dependencies={['payeHybridEnabled']}
                                className="mb-0"
                              >
                                <AppSelect
                                  placeholder="Select payslip template"
                                  options={PAYMENT_MODEL_TEMPLATES?.PAYE_HYBRID}
                                />
                              </Form.Item>
                            </div>
                            {!!watchValues?.hybridPayePayslipTemplate
                              ?.value && (
                              <div
                                className="cursor-pointer mb-[12px]"
                                onClick={() =>
                                  window.open(
                                    getDemoTemplateLayout(
                                      watchValues?.hybridPayePayslipTemplate
                                        ?.value || '',
                                      PAYMENT_MODEL_ENUM.PAYE_HYBRID,
                                      isIreland,
                                    ),
                                    '_blank',
                                  )
                                }
                              >
                                <Icons.IconEye />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </AppCard>

                {/* Umbrella Hybrid Section */}
                <AppCard className="p-[16px]">
                  <div className="flex items-center gap-x-[8px]">
                    <Form.Item name="umbrellaHybridEnabled" className="mb-0" valuePropName="checked">
                      <AppSwitch />
                    </Form.Item>
                    <div className="text-[14px] font-[500] text-[#5c656e]">
                      Umbrella Hybrid
                    </div>
                  </div>
                  {watchValues?.umbrellaHybridEnabled && (
                    <div className="mt-[24px]">
                      <Row gutter={16}>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Reclaim App Levy Costs"
                            name="umbrellaHybridReclaimAppLevyCosts"
                            dependencies={['umbrellaHybridEnabled']}
                          >
                            <AppRadioGroup options={yesNoOptions} />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <div className="flex items-end gap-x-[8px]">
                            <div className="flex-1">
                              <Form.Item
                                label="Payslip Template"
                                name="hybridPayslipTemplate"
                                dependencies={['umbrellaHybridEnabled']}
                                className="mb-0"
                              >
                                <AppSelect
                                  placeholder="Select payslip template"
                                  options={PAYMENT_MODEL_TEMPLATES?.HYBRID}
                                />
                              </Form.Item>
                            </div>
                            {!!watchValues?.hybridPayslipTemplate?.value && (
                              <div
                                className="cursor-pointer mb-[12px]"
                                onClick={() =>
                                  window.open(
                                    getDemoTemplateLayout(
                                      watchValues?.hybridPayslipTemplate
                                        ?.value || '',
                                      PAYMENT_MODEL_ENUM.HYBRID,
                                    ),
                                    '_blank',
                                  )
                                }
                              >
                                <Icons.IconEye />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </AppCard>

                {/* CIS Section */}
                <AppCard className="p-[16px]">
                  <div className="flex items-center gap-x-[8px]">
                    <Form.Item name="cisEnabled" className="mb-0" valuePropName="checked">
                      <AppSwitch />
                    </Form.Item>
                    <div className="text-[14px] font-[500] text-[#5c656e]">
                      CIS
                    </div>
                  </div>
                  {watchValues?.cisEnabled && (
                    <div className="mt-[24px]">
                      <Row gutter={[24, 24]}>
                        <Col span={24}>
                          <div className="flex items-end gap-x-[8px]">
                            <div className="flex-1">
                              <Form.Item
                                label="Self Bill Template"
                                name="cisPayslipTemplate"
                                dependencies={['cisEnabled']}
                                className="mb-0"
                              >
                                <AppSelect
                                  placeholder="Select self bill template"
                                  options={PAYMENT_MODEL_TEMPLATES?.CIS}
                                />
                              </Form.Item>
                            </div>
                            {!!watchValues?.cisPayslipTemplate?.value && (
                              <div
                                className="cursor-pointer mb-[12px]"
                                onClick={() =>
                                  window.open(
                                    getDemoCisTemplateLayout(
                                      watchValues?.cisPayslipTemplate?.value ||
                                        '',
                                    ),
                                    '_blank',
                                  )
                                }
                              >
                                <Icons.IconEye />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </AppCard>

                {/* PAYE FTH Section */}
                {hasPayeFTH && (
                  <AppCard className="p-[16px]">
                    <div className="flex items-center gap-x-[8px]">
                      <Form.Item name="payeFTHEnabled" className="mb-0" valuePropName="checked">
                        <AppSwitch />
                      </Form.Item>
                      <div className="text-[14px] font-[500] text-[#5c656e]">
                        PAYE FTH
                      </div>
                    </div>
                    {watchValues?.payeFTHEnabled && (
                      <div className="mt-[24px]">
                        <Row gutter={24}>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Tax Free Payment Description"
                              name="taxFreePaymentDesc"
                              dependencies={['payeFTHEnabled']}
                              rules={[
                                {
                                  required: true,
                                  message:
                                    'Tax Free Payment Description is required',
                                },
                              ]}
                            >
                              <AppInput placeholder="Enter" />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <div className="flex items-center gap-x-[8px]">
                              <div className="flex-1">
                                <Form.Item
                                  label="Payslip Template"
                                  name="payeFTHPayslipTemplate"
                                  dependencies={['payeFTHEnabled']}
                                >
                                  <AppSelect
                                    placeholder="Select self bill template"
                                    options={PAYMENT_MODEL_TEMPLATES?.PAYE_FTH}
                                  />
                                </Form.Item>
                              </div>
                              {!!watchValues?.payeFTHPayslipTemplate?.value && (
                                <div
                                  className="cursor-pointer mt-[10px]"
                                  onClick={() =>
                                    window.open(
                                      getDemoTemplateLayout(
                                        watchValues?.payeFTHPayslipTemplate
                                          ?.value || '',
                                        PAYMENT_MODEL_ENUM.PAYE_FTH,
                                        isIreland,
                                      ),
                                      '_blank',
                                    )
                                  }
                                >
                                  <Icons.IconEye />
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Margin/Umbrella Advance Adjustment?"
                              name="payeFthMarginAdjustment"
                              dependencies={['payeFTHEnabled']}
                            >
                              <AppRadioGroup options={yesNoOptions} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </AppCard>
                )}
                </>
              )}
              </>
            )}

            {isIreland && (
              <AppCard className="p-[16px]">
                <div className="flex items-center gap-x-[8px]">
                  <Form.Item name="cisEnabled" className="mb-0" valuePropName="checked">
                    <AppSwitch />
                  </Form.Item>
                  <div className="text-[14px] font-[500] text-[#5c656e]">
                    RCT
                  </div>
                </div>
                {watchValues?.cisEnabled && (
                  <div className="mt-[24px]">
                    <Row gutter={16}>
                      <Col sm={24} md={12}>
                        <div className="flex items-end gap-x-[8px]">
                          <div className="flex-1">
                            <Form.Item
                              label="Self Bill Template"
                              name="cisPayslipTemplate"
                              dependencies={['cisEnabled']}
                              className="mb-0"
                            >
                              <AppSelect
                                placeholder="Select self bill template"
                                options={PAYMENT_MODEL_TEMPLATES?.CIS}
                              />
                            </Form.Item>
                          </div>
                          {!!watchValues?.cisPayslipTemplate?.value && (
                            <div
                              className="cursor-pointer mb-[12px]"
                              onClick={() =>
                                window.open(RCT_DEFAULT_DEMO_LAYOUT, '_blank')
                              }
                            >
                              <Icons.IconEye />
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
              </AppCard>
            )}
          </div>
        </AppFormContainer>
      </div>
    </div>
  );
};

export default PaymentModel;
